import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { RemoveRedEye } from "@mui/icons-material";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import DeleteModal from "components/UI/Form/partials/Delete";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import { addUser, deleteUser, editUser, getQUsers, getUsers, QUsersCells, UsersCells } from "framework/user";
import phone from 'phone';
import { IDropDown, IQUser, IUser, IUserInput } from "models/user";
import { use } from "i18next";
import { formatDate } from "helpers";
import { getDropDown } from "framework/services";
import { useNavigate } from "react-router-dom";

interface Props {
  feature: Feature;
}


const QUsers: React.FC<Props> = ({ feature }) => {

  const navigate = useNavigate();


  const [filteredQUsers, setFilterQUsers] = useState<IQUser[]>([])

  // eslint-disable-next-line
  const queryClient = new QueryClient();


  const {
    loading,
    data: Users,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getQUsers(),
    fetchPermission: async () => null,
    section: "QUsers",
    onSuccess: (data) => setFilterQUsers(data),
  });


  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );



  const getBodyCells = (row: IQUser) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "firstName")
        return {
          id: "firstName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{`${row?.firstName} ${row?.lastName}`}</Typography>
            </TableCell>
          ),
        };
      if (key === "email")
        return {
          id: "email",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.email}</Typography>
            </TableCell>
          ),
        };

      if (key === "contactNumber")
        return {
          id: "contactNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.contactNumber}</Typography>
            </TableCell>
          ),
        };

      if (key === "dateOfBirth")
        return {
          id: "dateOfBirth",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{formatDate(row?.dateOfBirth)}</Typography>
            </TableCell>
          ),
        };

      if (key === "countryName")
        return {
          id: "countryName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryName}</Typography>
            </TableCell>
          ),
        };

      if (key === "cityName")
        return {
          id: "cityName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.cityName}</Typography>
            </TableCell>
          ),
        };

      if (key === "notes")
        return {
          id: "notes",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.notes}</Typography>
            </TableCell>
          ),
        };

      if (key === "referralSource")
        return {
          id: "referralSource",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.referralSource}</Typography>
            </TableCell>
          ),
        };

      if (key === "address")
        return {
          id: "address",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.address}</Typography>
            </TableCell>
          ),
        };

      if (key === "userId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
              >

                <Tooltip title={`Go to User Details`}>
                  <IconButton
                    size={"small"}
                    sx={{
                      color: "#9e9e52",
                    }}
                    onClick={() => navigate(`${row.userId}`)}
                  >
                    <RemoveRedEye />
                  </IconButton>
                </Tooltip>

              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };


  const handleSearch = (value: string) => {
    if (!Users) return;

    const searchTerm = value.trim().toLowerCase();

    if (!searchTerm) {
      setFilterQUsers(Users);
      return;
    }

    const filtered = Users.filter(({ firstName, lastName, contactNumber, cityName, countryName, email, notes, referralSource, address }) => {
      const fullText = `${firstName} ${lastName} ${contactNumber} ${cityName} ${countryName} ${email} ${notes} ${referralSource} ${address}`.toLowerCase();
      return fullText.includes(searchTerm);
    });

    setFilterQUsers(filtered);
  };


  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"

        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
            sx={{ mb: 4 }}
          >
            <Typography
              variant="h3"
              color={"header.primary"}
            >
              {(feature.name)}
            </Typography>


          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={"Search"} />
            </Stack>
          </Stack>
        </Grid>

        {filteredQUsers.length > 0 && (
          <List
            headCells={QUsersCells}
            data={filteredQUsers}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!filteredQUsers || filteredQUsers.length === 0) &&

          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={"No EConsultation Users"}
            message={`No EConsultation Users Exists Yet`}
          />
        }


      </Stack>


    </>
  );
};

export default QUsers;
